import { useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";

import { useChatStore } from "src/views/assistant/stores/ChatStore";
import { useWorkflowLeadPersona } from "src/views/products/workflow/hooks/useWorkflowLeadPersona";
import WorkflowPersonaSlotsPanel from "src/views/products/workflow/components/WorkflowPersonaSlotsPanel";
import { NucToast } from "src/reusable/components/Toast";

import PartsAISplashScreen from "./components/PartsAISplashScreen";
import { usePartsAIStore } from "./store/PartsAIStore";
import { useCreatePartsSearch } from "./services/PartsAISearchService";
import { PARTS_SOURCING_PRODUCT } from "./partsSourcingProduct";
import { PARTS_AI_SEARCH_LISTING_PRODUCT_KEY } from "./utils/chatUrl";

/**
 * Splash screen for starting a new Parts AI **search** dialogue.
 *
 * Differs from PartsAISplashPage in one place only: the submission flow.
 * Where the run flow creates a `parts_ai.run` and the comparison flow
 * creates a `parts_ai.comparison_dialogue`, this flow creates a
 * `parts_ai.search_dialogue` atomically via `POST /products/parts/searches`
 * (the server also creates the dialogue when `dialogue_id` is omitted),
 * then navigates to `/parts/chat/search/{dialogueId}`.
 */
interface PartsAISearchSplashLocationState {
  // Reserved for parity with the run flow's file-upload re-entry path.
  // Search dialogues don't currently support pre-uploaded files, but the
  // optional field keeps the location shape compatible.
  initialUploadDialogueId?: number;
}

// Module-level so the consuming components' effect dependency arrays don't
// churn. The splash never streams, so nothing to terminate.
const NOOP_TERMINATE_CONNECTION = () => {};

const PartsAISearchSplashPage = () => {
  const { isLoading: isLoadingPersona, leadSlot } = useWorkflowLeadPersona(
    PARTS_SOURCING_PRODUCT,
  );

  const {
    config: chatConfig,
    setActiveDialogueId,
    setInitialUserMessage,
    clearUploadedFiles,
    setProductConfig,
    registerChatUrlBuilder,
    isChatReady,
    resetToBaseConfig,
  } = useChatStore();

  const { setPartsAIRunId, setDialogueId, setDialogueKind, reset } =
    usePartsAIStore();
  const history = useHistory();
  const location = useLocation<PartsAISearchSplashLocationState>();

  // We deliberately do NOT call ``usePartsAIChat`` here. The splash never
  // streams, all the streaming state the splash forwards to
  // ``PartsAISplashScreen`` is constant here (no message sent,
  // no connection opened), so we pass static defaults.

  const { mutateAsync: createPartsSearch, isLoading: isCreatingSearch } =
    useCreatePartsSearch();

  const handleSubmit = async (userInput: string): Promise<boolean> => {
    if (isCreatingSearch) {
      return false;
    }
    if (!isChatReady()) {
      NucToast({
        message: "Chat is not ready. Please wait for configuration to load.",
        type: "error",
        hideProgressBar: false,
      });
      return false;
    }

    try {
      // Atomic create — server makes the dialogue and links the search row
      // in one round trip, mirroring the comparison-create endpoint.
      const search = await createPartsSearch({
        chat_config_id: chatConfig?.chatConfigId ?? null,
      });
      setDialogueId(search.dialogue_id);
      setPartsAIRunId(undefined);
      setDialogueKind("search");
      setInitialUserMessage(userInput);
      history.push(`/parts/chat/search/${search.dialogue_id}`);
      return true;
    } catch (error) {
      console.error(
        "[PartsAISearchSplashPage] Failed to create search dialogue:",
        error,
      );
      NucToast({
        message: "Failed to start search. Please try again.",
        type: "error",
        hideProgressBar: false,
      });
      return false;
    }
  };

  useEffect(() => {
    // Reset chat state on mount so we don't render stale data while the
    // user is composing their first query.
    setActiveDialogueId(null);
    clearUploadedFiles();
    // The composer only adds to the document scope, so scope left by a
    // previous chat (any product) is dropped here.
    useChatStore.getState().setSourceDataRecordIds([]);
    reset();
    setProductConfig({
      productKey: PARTS_SOURCING_PRODUCT.productKey,
      productName: PARTS_SOURCING_PRODUCT.displayName,
      basePath: PARTS_SOURCING_PRODUCT.basePath,
    });
    // Registered under the same composite key the recents strip queries with.
    registerChatUrlBuilder(
      PARTS_AI_SEARCH_LISTING_PRODUCT_KEY,
      async (dialogueId: number) => `/parts/chat/search/${dialogueId}`,
    );
  }, [
    setActiveDialogueId,
    clearUploadedFiles,
    reset,
    setProductConfig,
    registerChatUrlBuilder,
  ]);

  // Reset so an unresolved lead slot doesn't silently stamp a bad persona.
  const resolvedLeadSlug = leadSlot?.resolved_persona?.slug;
  useEffect(() => {
    if (!isLoadingPersona && !resolvedLeadSlug) {
      resetToBaseConfig();
    }
  }, [isLoadingPersona, resolvedLeadSlug, resetToBaseConfig]);

  // Suppress the unused-location warning while keeping the type wired for
  // future file-upload re-entry parity with the run splash page.
  void location;

  if (isLoadingPersona) {
    return null;
  }

  return (
    <PartsAISplashScreen
      chatStreamError=""
      isChatConnectionError={false}
      isChatLoading={isCreatingSearch}
      isChatEnded={false}
      isTurnActive={false}
      terminateConnection={NOOP_TERMINATE_CONNECTION}
      sendUserMessage={handleSubmit}
      placeholder="Describe the part you're looking for…"
      // Unions parts-ai (pre-split history) with parts-sourcing.
      recentsProductKey={PARTS_AI_SEARCH_LISTING_PRODUCT_KEY}
      recentsKind="search"
      // Recents live at the shared "/parts/recents", not this splash's own basePath.
      recentsBasePath="/parts"
      topActions={
        <WorkflowPersonaSlotsPanel product={PARTS_SOURCING_PRODUCT} />
      }
    />
  );
};

export default PartsAISearchSplashPage;
